// src/Services/powerUpService.js
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";

// ─── Item Catalog ────────────────────────────────────────────────────────────

export const POWER_UP_ITEMS = {
  // Offense — steal points
  steal_10: {
    id: "steal_10",
    type: "steal_points",
    amount: 10,
    cost: 25,
    category: "offense",
    name: "Pickpocket",
    emoji: "🤏",
    iconName: "hand",
    desc: "Steal 10 pts from a player's total",
    announced: true,
    requiresTarget: true,
    requiresCategory: false,
  },
  steal_20: {
    id: "steal_20",
    type: "steal_points",
    amount: 20,
    cost: 40,
    category: "offense",
    name: "Heist",
    emoji: "💰",
    iconName: "banknote",
    desc: "Steal 20 pts from a player's total",
    announced: true,
    requiresTarget: true,
    requiresCategory: false,
  },
  steal_30: {
    id: "steal_30",
    type: "steal_points",
    amount: 30,
    cost: 60,
    category: "offense",
    name: "Grand Larceny",
    emoji: "🦹",
    iconName: "skull",
    desc: "Steal 30 pts from a player's total",
    announced: true,
    requiresTarget: true,
    requiresCategory: false,
  },

  // Offense — scoring disruption
  no_repeats: {
    id: "no_repeats",
    type: "no_repeats",
    cost: 15,
    category: "offense",
    name: "No Copycats",
    emoji: "🚫",
    iconName: "ban",
    desc: "Target only gets 10 or 0 per answer — no shared 5s",
    announced: false,
    requiresTarget: true,
    requiresCategory: false,
  },
  score_freeze: {
    id: "score_freeze",
    type: "score_freeze",
    cost: 30,
    category: "offense",
    name: "Freeze",
    emoji: "🧊",
    iconName: "snowflake",
    desc: "Target scores 0 next round, no matter what",
    announced: false,
    requiresTarget: true,
    requiresCategory: false,
  },
  category_sniper: {
    id: "category_sniper",
    type: "category_sniper",
    cost: 20,
    category: "offense",
    name: "Category Sniper",
    emoji: "🎯",
    iconName: "crosshair",
    desc: "Zero out one category for a player next round",
    announced: false,
    requiresTarget: true,
    requiresCategory: true,
  },

  // Offense — score manipulation
  share_fate: {
    id: "share_fate",
    type: "share_fate",
    cost: 35,
    category: "offense",
    name: "Score Split",
    emoji: "⚖️",
    iconName: "scale",
    desc: "Average your total score with another player's right now",
    announced: true,
    requiresTarget: true,
    requiresCategory: false,
  },
  copycat: {
    id: "copycat",
    type: "copycat",
    cost: 25,
    category: "offense",
    name: "Mirror",
    emoji: "📋",
    iconName: "clipboard-copy",
    desc: "Copy a player's score in one category next round",
    announced: false,
    requiresTarget: true,
    requiresCategory: true,
  },

  // Defense
  shield: {
    id: "shield",
    type: "shield",
    cost: 25,
    category: "defense",
    name: "Shield",
    emoji: "🛡️",
    iconName: "shield",
    desc: "Block the next attack aimed at you",
    announced: true,
    requiresTarget: false,
    requiresCategory: false,
  },
};

// ─── Special round conflicts ──────────────────────────────────────────────────
// These item types are disabled (and refunded) when a special round is active
const SPECIAL_ROUND_INCOMPATIBLE = new Set([
  "steal_points",
  "score_freeze",
  "share_fate",
  "no_repeats",
]);

// ─── Purchase a power-up ─────────────────────────────────────────────────────
/**
 * Validates, spends coins, and writes the queued effect to Firestore.
 *
 * @param {object} opts
 * @param {string}  opts.itemId         - Key from POWER_UP_ITEMS
 * @param {string}  opts.targetUserId   - Target player's userId (offense only)
 * @param {string}  opts.targetName     - Target player's display name
 * @param {string}  opts.category       - Category name (category_sniper / copycat)
 * @param {string}  opts.roomDocId      - Firestore document ID
 * @param {object}  opts.roomData       - Current room snapshot
 * @param {object}  opts.coinStore      - Zustand coin store instance (pass the store methods)
 * @returns {{ success: boolean, message?: string }}
 */
export async function purchasePowerUp({
  itemId,
  targetUserId,
  targetName,
  category,
  roomDocId,
  roomData,
  coinStore,
}) {
  const item = POWER_UP_ITEMS[itemId];
  if (!item) return { success: false, message: "Unknown item." };

  const currentUserId = localStorage.getItem("currentUserId");
  const currentRound = roomData.currentRound;

  // ── Coin check ──────────────────────────────────────────────────────────
  if (!coinStore.canAfford(item.cost)) {
    return {
      success: false,
      message: `Not enough coins. Need ${item.cost}, you have ${coinStore.coinBalance}.`,
    };
  }

  // ── Round usage check ────────────────────────────────────────────────────
  const usage = roomData.roundItemUsage?.[currentUserId] || {};
  if (item.category === "offense" && usage.offenseUsed) {
    return {
      success: false,
      message: "You already used an offense item this round.",
    };
  }
  if (item.category === "defense" && usage.defenseUsed) {
    return {
      success: false,
      message: "You already used a defense item this round.",
    };
  }

  // ── Shield duplicate check ────────────────────────────────────────────────
  if (item.type === "shield" && roomData.shields?.[currentUserId]) {
    return { success: false, message: "You already have a shield." };
  }

  // ── Consecutive target check (max 2 rounds in a row on same person) ──────
  if (item.requiresTarget && targetUserId) {
    const consec = roomData.consecutiveTargets?.[currentUserId] || {};
    if (consec.lastTargetId === targetUserId && (consec.count || 0) >= 2) {
      return {
        success: false,
        message: `Can't target the same player 3 rounds in a row.`,
      };
    }
  }

  // ── Spend coins (optimistic via store) ───────────────────────────────────
  const spendResult = await coinStore.spendCoins(
    item.cost,
    "power_up",
    roomData.roomId
  );
  if (!spendResult.success) {
    return {
      success: false,
      message: spendResult.message || "Failed to spend coins.",
    };
  }

  // ── Write to Firestore ───────────────────────────────────────────────────
  const updates = {};

  if (item.type === "shield") {
    updates[`shields.${currentUserId}`] = true;
    updates[`roundItemUsage.${currentUserId}.defenseUsed`] = true;
  } else {
    const effect = {
      id: `${currentUserId}_${Date.now()}`,
      type: item.type,
      itemId,
      sourceUserId: currentUserId,
      sourceName: localStorage.getItem("playerName") || "Unknown",
      targetUserId: targetUserId || null,
      targetName: targetName || null,
      amount: item.amount || null,
      category: category || null,
      purchasedAtRound: currentRound,
      // share_fate applies immediately (this round), others apply next round
      appliesAtRound:
        item.type === "share_fate" ? currentRound : currentRound + 1,
      resolved: false,
    };

    updates.pendingEffects = arrayUnion(effect);
    updates[`roundItemUsage.${currentUserId}.offenseUsed`] = true;

    // Update consecutive target tracking
    const consec = roomData.consecutiveTargets?.[currentUserId] || {};
    if (consec.lastTargetId === targetUserId) {
      updates[`consecutiveTargets.${currentUserId}.count`] =
        (consec.count || 0) + 1;
    } else {
      updates[`consecutiveTargets.${currentUserId}`] = {
        lastTargetId: targetUserId,
        count: 1,
      };
    }
  }

  try {
    await updateDoc(doc(db, "rooms", roomDocId), updates);
    return { success: true };
  } catch (error) {
    // Refund coins if Firestore write failed
    await coinStore.addCoins(item.cost, { purpose: "power_up_refund" });
    console.error("Power-up purchase failed:", error);
    return { success: false, message: "Purchase failed. Coins refunded." };
  }
}

// ─── Apply pending effects inside calculateScores ────────────────────────────
/**
 * Applies all pending power-up effects for currentRound.
 * Modifies the `scores` object in place (for round-level effects).
 * Returns adjustments for cumulative-total effects (steal_points, share_fate).
 *
 * @param {object} scores       - Current round scores keyed by userId (modified in place)
 * @param {object} roomData     - Full room snapshot
 * @param {number} currentRound - The round number being finalized
 * @returns {{
 *   powerUpScores: object,   // { [userId]: number } cumulative adjustments
 *   effectsLog: object[],    // what happened (for announcements)
 *   resolvedIds: string[],   // effect IDs to remove from pendingEffects
 *   shieldUpdates: object,   // { [userId]: false } shields to consume
 *   refunds: object[],       // effects to refund { itemId, sourceUserId, cost }
 * }}
 */
export function applyPendingEffects(scores, roomData, currentRound) {
  const pending = (roomData.pendingEffects || []).filter(
    (e) => e.appliesAtRound === currentRound && !e.resolved
  );

  if (pending.length === 0) {
    return {
      powerUpScores: {},
      effectsLog: [],
      resolvedIds: [],
      shieldUpdates: {},
      refunds: [],
    };
  }

  // Check if this is a special round
  const specialRounds = roomData.specialRounds || [];
  const isSpecialRound = specialRounds.some((sr) => sr.round === currentRound);

  const powerUpScores = {}; // cumulative adjustments (steal, share_fate)
  const effectsLog = [];
  const resolvedIds = [];
  const shieldUpdates = {};
  const refunds = [];

  // Track shields state within this resolution pass (so one shield can only block once)
  const activeShields = { ...(roomData.shields || {}) };

  pending.forEach((effect) => {
    resolvedIds.push(effect.id);
    const { type, sourceUserId, targetUserId, amount, category, itemId } =
      effect;

    // ── Special round conflict: refund and skip ──────────────────────────
    if (isSpecialRound && SPECIAL_ROUND_INCOMPATIBLE.has(type)) {
      refunds.push({
        itemId,
        sourceUserId,
        cost: POWER_UP_ITEMS[itemId]?.cost || 0,
      });
      effectsLog.push({
        ...effect,
        round: currentRound,
        outcome: "refunded_special_round",
        announced: false,
      });
      return;
    }

    // ── Shield check ─────────────────────────────────────────────────────
    if (targetUserId && activeShields[targetUserId]) {
      // Consume the shield
      activeShields[targetUserId] = false;
      shieldUpdates[targetUserId] = false;
      effectsLog.push({
        ...effect,
        round: currentRound,
        outcome: "blocked_by_shield",
        announced: true,
      });
      return;
    }

    // ── Apply the effect ─────────────────────────────────────────────────
    switch (type) {
      case "steal_points": {
        const stolen = amount || 0;
        powerUpScores[sourceUserId] = (powerUpScores[sourceUserId] || 0) + stolen;
        powerUpScores[targetUserId] = (powerUpScores[targetUserId] || 0) - stolen;
        effectsLog.push({
          ...effect,
          round: currentRound,
          outcome: "applied",
          announced: true,
        });
        break;
      }

      case "score_freeze": {
        if (scores[targetUserId]) {
          // Zero every category and total
          const catKeys = Object.keys(scores[targetUserId]).filter(
            (k) =>
              !["total", "specialBonus", "specialEffect", "powerUpEffect"].includes(k)
          );
          catKeys.forEach((k) => {
            scores[targetUserId][k] = 0;
          });
          scores[targetUserId].total = 0;
          scores[targetUserId].powerUpEffect = "frozen";
        }
        effectsLog.push({
          ...effect,
          round: currentRound,
          outcome: "applied",
          announced: false,
        });
        break;
      }

      case "no_repeats": {
        if (scores[targetUserId]) {
          const catKeys = Object.keys(scores[targetUserId]).filter(
            (k) =>
              !["total", "specialBonus", "specialEffect", "powerUpEffect"].includes(k)
          );
          // Change any 5-point shared answers to 0
          catKeys.forEach((k) => {
            if (scores[targetUserId][k] === 5) {
              scores[targetUserId][k] = 0;
            }
          });
          // Recalculate total
          scores[targetUserId].total = catKeys.reduce(
            (sum, k) => sum + (scores[targetUserId][k] || 0),
            0
          );
          scores[targetUserId].powerUpEffect = "no_repeats";
        }
        effectsLog.push({
          ...effect,
          round: currentRound,
          outcome: "applied",
          announced: false,
        });
        break;
      }

      case "category_sniper": {
        if (scores[targetUserId] && category) {
          scores[targetUserId][category] = 0;
          const catKeys = Object.keys(scores[targetUserId]).filter(
            (k) =>
              !["total", "specialBonus", "specialEffect", "powerUpEffect"].includes(k)
          );
          scores[targetUserId].total = catKeys.reduce(
            (sum, k) => sum + (scores[targetUserId][k] || 0),
            0
          );
          scores[targetUserId].powerUpEffect = "sniped";
        }
        effectsLog.push({
          ...effect,
          round: currentRound,
          outcome: "applied",
          announced: false,
        });
        break;
      }

      case "share_fate": {
        // Average cumulative totals between source and target
        // Start with current round scores
        let sourceTotal = scores[sourceUserId]?.total || 0;
        let targetTotal = scores[targetUserId]?.total || 0;

        // Add all previous completed rounds (skip current round being calculated)
        Object.entries(roomData.rounds || {}).forEach(([roundNum, round]) => {
          if (parseInt(roundNum) === currentRound) return;
          if (round.scores?.[sourceUserId]) {
            sourceTotal += round.scores[sourceUserId].total || 0;
          }
          if (round.powerUpScores?.[sourceUserId]) {
            sourceTotal += round.powerUpScores[sourceUserId] || 0;
          }
          if (round.scores?.[targetUserId]) {
            targetTotal += round.scores[targetUserId].total || 0;
          }
          if (round.powerUpScores?.[targetUserId]) {
            targetTotal += round.powerUpScores[targetUserId] || 0;
          }
        });

        const avg = Math.floor((sourceTotal + targetTotal) / 2);
        // Adjustments are stored in powerUpScores (cumulative layer)
        powerUpScores[sourceUserId] =
          (powerUpScores[sourceUserId] || 0) + (avg - sourceTotal);
        powerUpScores[targetUserId] =
          (powerUpScores[targetUserId] || 0) + (avg - targetTotal);

        effectsLog.push({
          ...effect,
          round: currentRound,
          outcome: "applied",
          announced: true,
          sourceTotalBefore: sourceTotal,
          targetTotalBefore: targetTotal,
          newSharedTotal: avg,
        });
        break;
      }

      case "copycat": {
        if (scores[sourceUserId] && scores[targetUserId] && category) {
          scores[sourceUserId][category] = scores[targetUserId][category] || 0;
          const catKeys = Object.keys(scores[sourceUserId]).filter(
            (k) =>
              !["total", "specialBonus", "specialEffect", "powerUpEffect"].includes(k)
          );
          scores[sourceUserId].total = catKeys.reduce(
            (sum, k) => sum + (scores[sourceUserId][k] || 0),
            0
          );
          scores[sourceUserId].powerUpEffect = "copycat";
        }
        effectsLog.push({
          ...effect,
          round: currentRound,
          outcome: "applied",
          announced: false,
        });
        break;
      }

      default:
        break;
    }
  });

  return { powerUpScores, effectsLog, resolvedIds, shieldUpdates, refunds };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns true if this player already used an offense item this round */
export function hasUsedOffense(roomData, userId) {
  return roomData.roundItemUsage?.[userId]?.offenseUsed === true;
}

/** Returns true if this player already used a defense item this round */
export function hasUsedDefense(roomData, userId) {
  return roomData.roundItemUsage?.[userId]?.defenseUsed === true;
}

/** Returns true if the player has an active shield */
export function hasShield(roomData, userId) {
  return roomData.shields?.[userId] === true;
}

/** Returns true if same target has been attacked 2+ rounds in a row */
export function isConsecutiveTargetBlocked(roomData, attackerId, targetId) {
  // If only 2 players, don't block — there's only one possible target
  const playerCount = roomData.players?.length || 0;
  if (playerCount <= 2) return false;

  const consec = roomData.consecutiveTargets?.[attackerId] || {};
  return consec.lastTargetId === targetId && (consec.count || 0) >= 2;
}

/**
 * Compute overall standings including powerUpScores adjustments.
 * Drop-in replacement for the getOverallStandings logic in both screens.
 */
export function computeOverallStandings(roomData) {
  if (!roomData?.rounds || !roomData?.players) return [];

  const playerTotals = {};
  roomData.players.forEach((player) => {
    playerTotals[player.id] = { name: player.name, total: 0 };
  });

  Object.values(roomData.rounds).forEach((round) => {
    if (round.scores) {
      Object.entries(round.scores).forEach(([userId, categoryScores]) => {
        if (playerTotals[userId]) {
          playerTotals[userId].total += categoryScores.total || 0;
        }
      });
    }
    // Include power-up cumulative adjustments
    if (round.powerUpScores) {
      Object.entries(round.powerUpScores).forEach(([userId, adjustment]) => {
        if (playerTotals[userId]) {
          playerTotals[userId].total += adjustment || 0;
        }
      });
    }
  });

  return Object.values(playerTotals).sort((a, b) => b.total - a.total);
}
